/**
 * WordGame Component - Main Container
 *
 * Orchestrates the full Word Clash game lifecycle:
 * - Game mode selection, engine initialization, gameplay, game end
 * - Single-player: AI moves after player turn
 * - Local multiplayer: turn-based play
 * - Integrates with platform services
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { WordGameEngine } from '../WordGameEngine';
import { WordGameAI } from '../WordGameAI';
import { WordGameState, WordGameDifficulty, TilePlacement, Tile } from '../types';
import WordGameBoard from './WordGameBoard';
import UniversalGameModeSelector, { GameModeSelection } from '../../components/UniversalGameModeSelector';
import { ErrorBoundary } from '../../components/ErrorBoundary';
import { useAuthentication } from '../../hooks/useAuthentication';
import { useNotifications } from '../../hooks/useNotifications';
import { GameSessionManager } from '../../services/GameSessionManager';
import { NotificationType } from '../../services/NotificationService';
import './WordGame.css';

export interface WordGameProps {
  onExit?: () => void;
  initialRoomCode?: string;
}

type GameView = 'mode-select' | 'playing' | 'game-over';

const WordGameInner: React.FC<WordGameProps> = ({ onExit, initialRoomCode }) => {
  const [engine] = useState(() => new WordGameEngine());
  const [ai, setAi] = useState<WordGameAI | null>(null);
  const [gameState, setGameState] = useState<WordGameState | null>(null);
  const [view, setView] = useState<GameView>('mode-select');
  const [isAIThinking, setIsAIThinking] = useState(false);
  const [difficulty, setDifficulty] = useState<WordGameDifficulty>('medium');
  const [gameSessionId, setGameSessionId] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const aiTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const { isAuthenticated } = useAuthentication();
  const { addNotification } = useNotifications();

  const updateGameState = useCallback(() => {
    setGameState({ ...engine.getState() });
  }, [engine]);

  const handleStartGame = useCallback((selection: GameModeSelection) => {
    const diff = (selection.difficulty as WordGameDifficulty) || 'medium';
    setDifficulty(diff);

    if (selection.mode === 'ai') {
      engine.startGame(['You', 'AI'], [false, true]);
      setAi(new WordGameAI(diff));
    } else if (selection.mode === 'local') {
      engine.startGame(['Player 1', 'Player 2'], [false, false]);
      setAi(null);
    } else {
      // Online mode - start with AI for now
      engine.startGame(['You', 'AI'], [false, true]);
      setAi(new WordGameAI(diff));
    }

    updateGameState();
    setView('playing');

    const sessionId = GameSessionManager.getInstance().startGameSession('word-game');
    setGameSessionId(sessionId);

    addNotification({
      type: NotificationType.SUCCESS,
      title: 'Word Clash',
      message: 'Game started! Place your first word through the center star.',
      duration: 4000,
    });
  }, [engine, updateGameState, addNotification]);

  const handlePlayWord = useCallback((placements: TilePlacement[]) => {
    if (!gameState || gameState.phase !== 'playing') return;

    const result = engine.playWord(gameState.currentPlayerIndex, placements);
    if (result.success) {
      setErrorMessage(null);
      updateGameState();

      if (result.words && result.words.length > 0) {
        const wordList = result.words.map(w => `${w.word.toUpperCase()} (${w.score})`).join(', ');
        addNotification({
          type: NotificationType.SUCCESS,
          title: `+${result.score} points!`,
          message: wordList,
          duration: 3000,
        });
      }

      // Check game over
      const newState = engine.getState();
      if (newState.phase === 'ended') {
        setView('game-over');
        if (gameSessionId) {
          GameSessionManager.getInstance().endGameSession(gameSessionId, true);
        }
        return;
      }

      // Trigger AI move if next player is AI
      if (ai && newState.players[newState.currentPlayerIndex].isAI) {
        triggerAIMove();
      }
    } else {
      setErrorMessage(result.error || 'Invalid move');
    }
  }, [gameState, engine, ai, updateGameState, addNotification, gameSessionId]);

  const handleExchangeTiles = useCallback((tileIds: string[]) => {
    if (!gameState || gameState.phase !== 'playing') return;

    const result = engine.exchangeTiles(gameState.currentPlayerIndex, tileIds);
    if (result.success) {
      setErrorMessage(null);
      updateGameState();
      addNotification({
        type: NotificationType.INFO,
        title: 'Tiles Exchanged',
        message: `Exchanged ${tileIds.length} tile(s). Turn passed.`,
        duration: 2000,
      });

      const newState = engine.getState();
      if (ai && newState.players[newState.currentPlayerIndex].isAI) {
        triggerAIMove();
      }
    } else {
      setErrorMessage(result.error || 'Cannot exchange tiles');
    }
  }, [gameState, engine, ai, updateGameState, addNotification]);

  const handlePassTurn = useCallback(() => {
    if (!gameState || gameState.phase !== 'playing') return;

    const result = engine.passTurn(gameState.currentPlayerIndex);
    if (result.success) {
      setErrorMessage(null);
      updateGameState();

      const newState = engine.getState();
      if (newState.phase === 'ended') {
        setView('game-over');
        if (gameSessionId) {
          GameSessionManager.getInstance().endGameSession(gameSessionId, true);
        }
        return;
      }

      if (ai && newState.players[newState.currentPlayerIndex].isAI) {
        triggerAIMove();
      }
    }
  }, [gameState, engine, ai, updateGameState, gameSessionId]);

  const triggerAIMove = useCallback(() => {
    setIsAIThinking(true);
    aiTimeoutRef.current = setTimeout(() => {
      const state = engine.getState();
      if (!ai || state.phase !== 'playing') {
        setIsAIThinking(false);
        return;
      }

      const move = ai.findBestMove(state);

      if (move.type === 'play' && move.placement) {
        const result = engine.playWord(state.currentPlayerIndex, move.placement.tiles);
        if (result.success && result.words) {
          const wordList = result.words.map(w => w.word.toUpperCase()).join(', ');
          addNotification({
            type: NotificationType.INFO,
            title: `AI played: ${wordList}`,
            message: `+${result.score} points`,
            duration: 3000,
          });
        } else {
          // AI failed to play, pass instead
          engine.passTurn(state.currentPlayerIndex);
        }
      } else if (move.type === 'exchange' && move.exchangeTiles) {
        engine.exchangeTiles(state.currentPlayerIndex, move.exchangeTiles.map(t => t.id));
      } else {
        engine.passTurn(state.currentPlayerIndex);
      }

      setIsAIThinking(false);
      updateGameState();

      const newState = engine.getState();
      if (newState.phase === 'ended') {
        setView('game-over');
        if (gameSessionId) {
          GameSessionManager.getInstance().endGameSession(gameSessionId, true);
        }
      }
    }, 1000 + Math.random() * 500);
  }, [engine, ai, updateGameState, addNotification, gameSessionId]);

  const handleNewGame = useCallback(() => {
    if (aiTimeoutRef.current) {
      clearTimeout(aiTimeoutRef.current);
    }
    setView('mode-select');
    setGameState(null);
    setErrorMessage(null);
    setIsAIThinking(false);
  }, []);

  useEffect(() => {
    return () => {
      if (aiTimeoutRef.current) {
        clearTimeout(aiTimeoutRef.current);
      }
    };
  }, []);

  if (view === 'mode-select') {
    return (
      <div className="word-game">
        <div className="word-game__header">
          <button className="word-game__back-btn" onClick={onExit}>
            &larr; Back to Hub
          </button>
          <h1 className="word-game__title">Word Clash</h1>
        </div>
        <UniversalGameModeSelector
          config={{
            gameName: 'Word Clash',
            gameIcon: '\uD83D\uDCDD',
            availableModes: ['ai', 'local', 'online'],
          }}
          onStart={handleStartGame}
          onCreateRoom={() => {}}
          onJoinRoom={() => {}}
          guestGamesRemaining={3}
          isAuthenticated={isAuthenticated}
          isLoading={false}
        />
      </div>
    );
  }

  if (view === 'game-over' && gameState) {
    const winner = gameState.players.reduce((best, p) => p.score > best.score ? p : best, gameState.players[0]);
    return (
      <div className="word-game">
        <div className="word-game__game-over">
          <h2 className="word-game__game-over-title">Game Over!</h2>
          <div className="word-game__final-scores">
            {gameState.players.map((p, i) => (
              <div key={i} className={`word-game__score-row ${p.id === winner.id ? 'word-game__score-row--winner' : ''}`}>
                <span className="word-game__player-name">{p.name}</span>
                <span className="word-game__player-score">{p.score} pts</span>
                {p.id === winner.id && <span className="word-game__winner-badge">Winner!</span>}
              </div>
            ))}
          </div>
          {gameState.gameOverReason && (
            <p className="word-game__end-reason">{gameState.gameOverReason}</p>
          )}
          <div className="word-game__game-over-actions">
            <button className="word-game__btn word-game__btn--primary" onClick={handleNewGame}>
              New Game
            </button>
            <button className="word-game__btn word-game__btn--secondary" onClick={onExit}>
              Back to Hub
            </button>
          </div>
        </div>
      </div>
    );
  }

  if (!gameState) return null;

  return (
    <div className="word-game">
      <div className="word-game__header">
        <button className="word-game__back-btn" onClick={onExit}>
          &larr; Exit
        </button>
        <h1 className="word-game__title">Word Clash</h1>
        <div className="word-game__scores">
          {gameState.players.map((p, i) => (
            <div
              key={i}
              className={`word-game__score-badge ${i === gameState.currentPlayerIndex ? 'word-game__score-badge--active' : ''}`}
            >
              <span>{p.name}</span>
              <strong>{p.score}</strong>
            </div>
          ))}
        </div>
      </div>

      {isAIThinking && (
        <div className="word-game__ai-thinking">
          AI is thinking...
        </div>
      )}

      {errorMessage && (
        <div className="word-game__error">
          {errorMessage}
          <button onClick={() => setErrorMessage(null)}>&times;</button>
        </div>
      )}

      <WordGameBoard
        gameState={gameState}
        onPlayWord={handlePlayWord}
        onExchangeTiles={handleExchangeTiles}
        onPassTurn={handlePassTurn}
        disabled={isAIThinking || gameState.players[gameState.currentPlayerIndex].isAI}
        currentPlayerIndex={gameState.currentPlayerIndex}
      />

      <div className="word-game__info-bar">
        <span>Tiles remaining: {gameState.tileBag.length}</span>
        <span>Turn: {gameState.turnNumber}</span>
      </div>
    </div>
  );
};

const WordGame: React.FC<WordGameProps> = (props) => (
  <ErrorBoundary componentName="WordGame">
    <WordGameInner {...props} />
  </ErrorBoundary>
);

export default WordGame;
